import { useEffect } from 'react';
import { Ionicons } from '@expo/vector-icons';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { getTransportModeConfig } from '@/types/transport';
import type { RouteLeg, RoutePlanStop } from '@/types/route-plan';

export const STEP_ROW_HEIGHT = 64;

const DOT_SIZE = 3;
const DOT_GAP = 5;
const DOT_SPAN = DOT_SIZE + DOT_GAP;

type RouteStepPagerRowProps = {
  leg: RouteLeg;
  stop?: RoutePlanStop;
  stepNumber: number;              // 1-based across all legs
  isCompleted: boolean;
  isCurrent: boolean;
  isLast: boolean;
  animateConnector: boolean;       // this row's connector leads into the current step
  guidance?: string | null;
  onToggle: (legId: string) => void;
  onSkip: (legId: string) => void;
};

/**
 * ConnectorDots — the dotted line segment below a step-number badge. When
 * animated (the segment leading into the current step) the dots flow
 * downward in a loop to show the route is active.
 */
function ConnectorDots({ color, animate }: { color: string; animate: boolean }) {
  const offset = useSharedValue(0);

  useEffect(() => {
    offset.value = 0;
    if (animate) {
      offset.value = withRepeat(
        withTiming(DOT_SPAN, { duration: 700, easing: Easing.linear }),
        -1,
        false,
      );
    }
  }, [animate, offset]);

  const flowStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: offset.value }],
  }));

  return (
    <View style={styles.connector}>
      <Animated.View style={[styles.connectorDots, animate && flowStyle]}>
        {[0, 1, 2, 3, 4, 5].map((dotIndex) => (
          <View key={dotIndex} style={[styles.dot, { backgroundColor: color }]} />
        ))}
      </Animated.View>
    </View>
  );
}

/**
 * RouteStepPagerRow — one numbered step in the home card's step navigator.
 * Current step is highlighted; completed steps are greyed and struck
 * through; upcoming steps render at lower opacity. Checkbox tap toggles
 * tick/untick; long-press the content skips.
 */
export function RouteStepPagerRow({
  leg,
  stop,
  stepNumber,
  isCompleted,
  isCurrent,
  isLast,
  animateConnector,
  guidance,
  onToggle,
  onSkip,
}: RouteStepPagerRowProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  const label =
    leg.kind === 'stay'
      ? `${leg.arriveTime}  ${stop?.title ?? 'Stop'}`
      : `${leg.departTime}  ${getTransportModeConfig(leg.mode).label} (${leg.durationMinutes} min)`;

  const badgeBackground = isCurrent ? palette.primary : 'transparent';
  const badgeBorder = isCurrent
    ? palette.primary
    : isCompleted
      ? palette.borderMuted
      : palette.border;
  const badgeTextColor = isCurrent
    ? colorScheme === 'dark'
      ? '#000000'
      : '#FFFFFF'
    : palette.textMuted;
  const rowOpacity = isCurrent ? 1 : isCompleted ? 0.45 : 0.6;

  return (
    <View style={[styles.row, { height: STEP_ROW_HEIGHT }]}>
      <View style={styles.rail}>
        <View
          style={[styles.badge, { backgroundColor: badgeBackground, borderColor: badgeBorder }]}
        >
          <Text style={[styles.badgeText, { color: badgeTextColor, fontFamily: getBodyFont() }]}>
            {stepNumber}
          </Text>
        </View>
        {!isLast && (
          <ConnectorDots
            color={animateConnector ? palette.primary : palette.borderMuted}
            animate={animateConnector}
          />
        )}
      </View>

      <View style={[styles.content, { opacity: rowOpacity }]}>
        <View
          style={[
            styles.card,
            { backgroundColor: isCurrent ? palette.primary + '15' : 'transparent' },
            isCurrent && { borderColor: palette.primary, borderWidth: 1 },
          ]}
        >
          <TouchableOpacity onPress={() => onToggle(leg.id)} hitSlop={10} activeOpacity={0.7}>
            <Ionicons
              name={isCompleted ? 'checkmark-circle' : 'ellipse-outline'}
              size={20}
              color={isCompleted || isCurrent ? palette.primary : palette.textMuted}
            />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.textBlock}
            onLongPress={() => !isCompleted && onSkip(leg.id)}
            delayLongPress={400}
            activeOpacity={1}
          >
            <Text
              style={[
                styles.label,
                { color: isCompleted ? palette.textMuted : palette.text, fontFamily: getBodyFont() },
                isCompleted && styles.completedLabel,
              ]}
              numberOfLines={1}
            >
              {label}
            </Text>
            {isCurrent && guidance ? (
              <Text
                style={[styles.guidance, { color: palette.primary, fontFamily: getBodyFont() }]}
                numberOfLines={1}
              >
                {guidance}
              </Text>
            ) : null}
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    gap: Spacing.sm,
  },
  rail: {
    width: 28,
    alignItems: 'center',
  },
  badge: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '700',
  },
  connector: {
    flex: 1,
    width: DOT_SIZE,
    marginVertical: 2,
    overflow: 'hidden',
  },
  connectorDots: {
    alignItems: 'center',
    gap: DOT_GAP,
    marginTop: -DOT_SPAN,
  },
  dot: {
    width: DOT_SIZE,
    height: DOT_SIZE,
    borderRadius: DOT_SIZE / 2,
  },
  content: {
    flex: 1,
    justifyContent: 'flex-start',
  },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
    padding: Spacing.sm,
    borderRadius: BorderRadius.md,
  },
  textBlock: {
    flex: 1,
    gap: 2,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
  },
  completedLabel: {
    textDecorationLine: 'line-through',
  },
  guidance: {
    fontSize: 12,
    fontWeight: '600',
  },
});
